import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FormikErrors, FormikTouched } from "formik";
import dayjs from "dayjs";
import { CargoPublicoData, DependenciasData, RelationshipData } from "@interfaces/catalogs";
import { FieldWrapper } from "./FieldWrapper";
import { ChoiceToggle } from "./ChoiceToggle";

/**
 * Una persona políticamente expuesta capturada en el expediente. Los nombres de los campos son
 * los de la vista; el mapeo al payload de `/peps` (nombre completo en `name`, `active`, etc.) lo
 * hace la pantalla que lo consume.
 */
export interface PepPersonValue {
  /** Solo aplica a los familiares; los socios no declaran parentesco. */
  parentesco: string;
  nombre: string;
  ap_paterno: string;
  ap_materno: string;
  dependencia: string;
  cargo: string;
  /** "true" | "false" — si sigue en el cargo. En "false" se exige la fecha de término. */
  actualmente: string;
  start_date: string;
  end_date: string;
  /** Id del registro en `/peps`; vacío mientras no se haya guardado. */
  id: string;
}

interface Props {
  /** Prefijo del campo en formik, ej. `peps_socio[0]`. */
  namePrefix: string;
  value: PepPersonValue;
  touched: FormikTouched<PepPersonValue> | undefined;
  errors: FormikErrors<PepPersonValue> | undefined;
  dependencias: DependenciasData[];
  cargos: CargoPublicoData[];
  /** Catálogo de parentescos; solo se pasa en la lista de familiares. */
  parentescos?: RelationshipData[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (event: React.FocusEvent) => void;
  setFieldValue: (field: string, value: string) => void;
  onRemove: () => void;
  disabled: boolean;
  /** Prefijo de los `data-testid` de la entrada, ej. `expediente-solicitud-pep-socio-0`. */
  testIdPrefix: string;
}

const errorOf = (
  touched: FormikTouched<PepPersonValue> | undefined,
  errors: FormikErrors<PepPersonValue> | undefined,
  field: keyof PepPersonValue,
) => (touched?.[field] && errors?.[field] ? String(errors[field]) : "");

/**
 * Bloque de captura de una persona PEP (socio/accionista o familiar). El de familiares es el mismo
 * más el parentesco, por eso el catálogo es el que decide si se pinta ese campo.
 */
export const PepPersonFields = ({
  namePrefix,
  value,
  touched,
  errors,
  dependencias,
  cargos,
  parentescos,
  onChange,
  onBlur,
  setFieldValue,
  onRemove,
  disabled,
  testIdPrefix,
}: Props) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
      {parentescos && (
        <FieldWrapper label="Parentesco" full error={errorOf(touched, errors, "parentesco")}>
          <TextField
            autoComplete="off"
            size="lendia_input"
            select
            fullWidth
            name={`${namePrefix}.parentesco`}
            value={value.parentesco}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            inputProps={{ "data-testid": `${testIdPrefix}-parentesco-select` }}
          >
            <MenuItem value="">Selecciona…</MenuItem>
            {parentescos.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </FieldWrapper>
      )}

      <FieldWrapper label="Nombre(s)" full error={errorOf(touched, errors, "nombre")}>
        <TextField
          autoComplete="off"
          size="lendia_input"
          fullWidth
          name={`${namePrefix}.nombre`}
          value={value.nombre}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Nombre(s)"
          disabled={disabled}
          inputProps={{ maxLength: 100, "data-testid": `${testIdPrefix}-nombre-input` }}
        />
      </FieldWrapper>

      <FieldWrapper label="Apellido paterno" error={errorOf(touched, errors, "ap_paterno")}>
        <TextField
          autoComplete="off"
          size="lendia_input"
          fullWidth
          name={`${namePrefix}.ap_paterno`}
          value={value.ap_paterno}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Apellido paterno"
          disabled={disabled}
          inputProps={{ maxLength: 100, "data-testid": `${testIdPrefix}-ap-paterno-input` }}
        />
      </FieldWrapper>

      <FieldWrapper label="Apellido materno" error={errorOf(touched, errors, "ap_materno")}>
        <TextField
          autoComplete="off"
          size="lendia_input"
          fullWidth
          name={`${namePrefix}.ap_materno`}
          value={value.ap_materno}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Apellido materno"
          disabled={disabled}
          inputProps={{ maxLength: 100, "data-testid": `${testIdPrefix}-ap-materno-input` }}
        />
      </FieldWrapper>

      <FieldWrapper label="Dependencia del cargo" error={errorOf(touched, errors, "dependencia")}>
        <TextField
          autoComplete="off"
          size="lendia_input"
          select
          fullWidth
          name={`${namePrefix}.dependencia`}
          value={value.dependencia}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          inputProps={{ "data-testid": `${testIdPrefix}-dependencia-select` }}
        >
          <MenuItem value="">Selecciona…</MenuItem>
          {dependencias.map((item) => (
            <MenuItem key={item.id} value={item.name}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      </FieldWrapper>

      <FieldWrapper label="Cargo público" error={errorOf(touched, errors, "cargo")}>
        <TextField
          autoComplete="off"
          size="lendia_input"
          select
          fullWidth
          name={`${namePrefix}.cargo`}
          value={value.cargo}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          inputProps={{ "data-testid": `${testIdPrefix}-cargo-select` }}
        >
          <MenuItem value="">Selecciona…</MenuItem>
          {cargos.map((item) => (
            <MenuItem key={item.id} value={item.name}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      </FieldWrapper>

      <FieldWrapper label="¿Actualmente está en ese cargo?" full error={errorOf(touched, errors, "actualmente")}>
        <ChoiceToggle
          value={value.actualmente}
          onChange={(nuevo) => {
            setFieldValue(`${namePrefix}.actualmente`, String(nuevo));
            // Sigue en el cargo: la fecha de término deja de aplicar.
            if (nuevo === "true") {
              setFieldValue(`${namePrefix}.end_date`, "");
            }
          }}
          options={[
            { value: "true", label: "Sí" },
            { value: "false", label: "No" },
          ]}
          disabled={disabled}
          size="small"
        />
      </FieldWrapper>

      <FieldWrapper label="Fecha de inicio" error={errorOf(touched, errors, "start_date")}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DatePicker
            value={value.start_date ? dayjs(value.start_date) : null}
            onChange={(date) => setFieldValue(`${namePrefix}.start_date`, dayjs(date).format("YYYY-MM-DD"))}
            maxDate={dayjs()}
            disabled={disabled}
            slotProps={{
              textField: {
                size: "lendia_input",
                fullWidth: true,
                name: `${namePrefix}.start_date`,
                onBlur,
                inputProps: { "data-testid": `${testIdPrefix}-fecha-inicio-input` },
              },
            }}
          />
        </LocalizationProvider>
      </FieldWrapper>

      {value.actualmente === "false" && (
        <FieldWrapper label="Fecha de término" error={errorOf(touched, errors, "end_date")}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              value={value.end_date ? dayjs(value.end_date) : null}
              onChange={(date) => setFieldValue(`${namePrefix}.end_date`, dayjs(date).format("YYYY-MM-DD"))}
              minDate={value.start_date ? dayjs(value.start_date) : undefined}
              maxDate={dayjs()}
              disabled={disabled}
              slotProps={{
                textField: {
                  size: "lendia_input",
                  fullWidth: true,
                  name: `${namePrefix}.end_date`,
                  onBlur,
                  inputProps: { "data-testid": `${testIdPrefix}-fecha-termino-input` },
                },
              }}
            />
          </LocalizationProvider>
        </FieldWrapper>
      )}
    </Box>

    <Typography
      component="button"
      type="button"
      variant="body2"
      onClick={onRemove}
      disabled={disabled}
      data-testid={`${testIdPrefix}-remove-button`}
      sx={{
        alignSelf: "flex-start",
        background: "none",
        border: "none",
        p: 0,
        cursor: "pointer",
        color: "error.main",
        fontWeight: 500,
        "&:disabled": { cursor: "not-allowed", opacity: 0.5 },
      }}
    >
      − Remover persona
    </Typography>
  </Box>
);
